import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaClipboardList, FaCopy, FaSearch, FaSyncAlt } from "react-icons/fa";
import http from "../api/apiClient";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import { useCan } from "../authz/useCan";
import { translateClassName } from "../utils/classroomUtils";
import { formatDutchDate } from "../utils/dutchDate";
import {
  LdsOverviewEmpty,
  LdsOverviewHero,
  LdsOverviewPage,
  LdsOverviewSection,
} from "../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";

type DuoChild = {
  id: string;
  firstName?: string;
  lastName?: string;
  dateBirth?: string | number[];
  duoRegisteredAt?: string | null;
  classrooms?: Array<{
    current?: boolean;
    className?: string;
    classroomName?: string;
    teacherFirstName?: string;
    teacherLastName?: string;
  }>;
};

export default function DuoRegistrations() {
  const { t, i18n } = useTranslation();
  const canViewBsn = useCan("children.bsn", "view");
  const [children, setChildren] = useState<DuoChild[]>([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"pending" | "completed" | "all">("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [copiedChildId, setCopiedChildId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const completed = filter === "all" ? "" : `?completed=${filter === "completed"}`;
      const { data } = await http.get(`/reports/duo-registrations${completed}`);
      setChildren(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || t("duo.load_error", { defaultValue: "DUO registrations could not be loaded." }));
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    const query = search.trim().toLocaleLowerCase(i18n.resolvedLanguage || i18n.language || "en");
    return children
      .filter(child => {
        if (!query) return true;
        const classroom = child.classrooms?.find(item => item.current);
        const searchable = [
          child.firstName,
          child.lastName,
          classroom?.className,
          classroom?.classroomName,
          classroom?.teacherFirstName,
          classroom?.teacherLastName,
        ].filter(Boolean).join(" ").toLocaleLowerCase(i18n.resolvedLanguage || i18n.language || "en");
        return searchable.includes(query);
      })
      .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));
  }, [children, i18n.language, i18n.resolvedLanguage, search]);

  const toggle = async (child: DuoChild) => {
    await http.patch(`/reports/duo-registrations/${child.id}?completed=${!child.duoRegisteredAt}`);
    window.dispatchEvent(new CustomEvent("lumen:duo-registration-changed"));
    await load();
  };

  const allSelected = sorted.length > 0 && sorted.every(child => selectedIds.has(child.id));
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(sorted.map(child => child.id)));
  };
  const toggleSelected = (childId: string) => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      if (next.has(childId)) next.delete(childId); else next.add(childId);
      return next;
    });
  };
  const completeSelected = async () => {
    if (selectedIds.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      await http.patch("/reports/duo-registrations/bulk", {
        childIds: Array.from(selectedIds),
        completed: filter !== "completed",
      });
      setSelectedIds(new Set());
      window.dispatchEvent(new CustomEvent("lumen:duo-registration-changed"));
      await load();
    } finally {
      setBulkBusy(false);
    }
  };
  const copyBsn = async (child: DuoChild) => {
    const { data } = await http.get(`/reports/duo-registrations/${child.id}/bsn-for-copy`);
    const bsn = typeof data === "string" ? data : data?.bsn;
    if (!bsn) throw new Error("BSN is unavailable");
    await navigator.clipboard.writeText(String(bsn));
    setCopiedChildId(child.id);
    window.setTimeout(() => setCopiedChildId(current => current === child.id ? null : current), 1800);
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorPage message={error} status={500} onRetry={load} />;

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaClipboardList}
        eyebrow={t("reports.title", { defaultValue: "Reports" })}
        title={t("duo.title", { defaultValue: "DUO registrations" })}
        description={t("duo.description", { defaultValue: "Process pupils with a commitment who still need to be registered with DUO." })}
        actions={(
          <span className="inline-flex min-w-12 items-center justify-center rounded-full bg-amber-400 px-4 py-2 text-lg font-bold text-amber-950 shadow-sm">
            {children.length}
          </span>
        )}
      />

      <LdsOverviewSection
        title={t("duo.worklist", { defaultValue: "DUO worklist" })}
        description={t("duo.report_hint", { defaultValue: "View pupils who are ready for DUO registration." })}
      >
        <div className="mb-5 flex flex-wrap gap-2 rounded-2xl border border-slate-200/80 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-900/70">
          <label className="relative min-w-[260px] flex-1 basis-full lg:basis-auto">
            <span className="sr-only">{t("duo.search_placeholder", { defaultValue: "Search pupils, classrooms or teachers" })}</span>
            <FaSearch aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={t("duo.search_placeholder", { defaultValue: "Search pupils, classrooms or teachers" })}
              aria-label={t("duo.search_placeholder", { defaultValue: "Search pupils, classrooms or teachers" })}
              className="h-11 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-indigo-400 dark:focus:ring-indigo-950"
            />
          </label>
          {(["pending", "completed", "all"] as const).map(value => (
            <button key={value} onClick={() => setFilter(value)} className={`rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-950 ${filter === value ? "bg-indigo-600 text-white shadow-sm" : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-indigo-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700"}`}>
              {t(`duo.filters.${value}`, { defaultValue: value === "pending" ? "Pending" : value === "completed" ? "Completed" : "All" })}
            </button>
          ))}
          <button onClick={load} className="ml-auto inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-indigo-50 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:bg-slate-950 dark:text-indigo-200 dark:ring-slate-700 dark:focus:ring-indigo-950"><FaSyncAlt />{t("common.refresh", { defaultValue: "Refresh" })}</button>
        </div>

        {selectedIds.size > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-700/60 dark:bg-amber-950/30">
            <span className="text-sm font-semibold text-amber-950 dark:text-amber-100">
              {t("duo.selected_count", { defaultValue: "{{count}} selected", count: selectedIds.size })}
            </span>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={completeSelected}
              className="ml-auto inline-flex items-center gap-2 rounded-full bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaCheck />
              {filter === "completed"
                ? t("duo.bulk_undo", { defaultValue: "Undo selected registrations" })
                : t("duo.bulk_complete", { defaultValue: "Mark selected as registered" })}
            </button>
          </div>
        )}

        {sorted.length === 0 ? (
          <LdsOverviewEmpty>{t("duo.empty", { defaultValue: "No pupils match this filter." })}</LdsOverviewEmpty>
        ) : (
          <LdsTableCard>
            <LdsTableScroll>
              <LdsTable className="min-w-[760px]">
                <LdsTableHead>
                  <LdsTableRow>
                    <LdsTableHeadCell className="w-12">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label={t("duo.select_all", { defaultValue: "Select all pupils" })} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                    </LdsTableHeadCell>
                    <LdsTableHeadCell>{t("duo.student", { defaultValue: "Pupil" })}</LdsTableHeadCell>
                    <LdsTableHeadCell>{t("duo.birthdate", { defaultValue: "Date of birth" })}</LdsTableHeadCell>
                    <LdsTableHeadCell>{t("duo.classroom", { defaultValue: "Classroom" })}</LdsTableHeadCell>
                    <LdsTableHeadCell>{t("duo.teacher", { defaultValue: "Teacher" })}</LdsTableHeadCell>
                    <LdsTableHeadCell>{t("duo.bsn", { defaultValue: "BSN" })}</LdsTableHeadCell>
                    <LdsTableHeadCell className="text-right">{t("duo.status", { defaultValue: "Status" })}</LdsTableHeadCell>
                  </LdsTableRow>
                </LdsTableHead>
                <LdsTableBody>
                  {sorted.map(child => {
                    const classroom = child.classrooms?.find(item => item.current);
                    const teacherName = [classroom?.teacherFirstName, classroom?.teacherLastName]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <LdsTableRow key={child.id}>
                        <LdsTableCell>
                          <input type="checkbox" checked={selectedIds.has(child.id)} onChange={() => toggleSelected(child.id)} aria-label={t("duo.select_student", { defaultValue: "Select {{name}}", name: `${child.firstName || ""} ${child.lastName || ""}`.trim() })} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                        </LdsTableCell>
                        <LdsTableCell className="font-semibold text-slate-950 dark:text-white">{child.firstName} {child.lastName}</LdsTableCell>
                        <LdsTableCell className="whitespace-nowrap">{formatDutchDate(child.dateBirth)}</LdsTableCell>
                        <LdsTableCell>{translateClassName(classroom) || t("duo.no_classroom", { defaultValue: "No classroom assigned" })}</LdsTableCell>
                        <LdsTableCell>{teacherName || t("duo.no_teacher", { defaultValue: "No teacher assigned" })}</LdsTableCell>
                        <LdsTableCell>
                          {canViewBsn ? (
                            <button type="button" onClick={() => copyBsn(child)} className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-200 transition hover:bg-indigo-50 dark:text-indigo-200 dark:ring-indigo-700 dark:hover:bg-indigo-950/50">
                              <FaCopy />{copiedChildId === child.id ? t("duo.bsn_copied", { defaultValue: "Copied" }) : t("duo.copy_bsn", { defaultValue: "Copy BSN" })}
                            </button>
                          ) : "—"}
                        </LdsTableCell>
                        <LdsTableCell className="text-right">
                          <button onClick={() => toggle(child)} className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-4 focus:ring-green-100 dark:focus:ring-green-950 ${child.duoRegisteredAt ? "border border-green-300 bg-white text-green-700 dark:bg-slate-950 dark:text-green-300" : "bg-green-600 text-white shadow-sm hover:bg-green-700"}`}>
                            <FaCheck />{child.duoRegisteredAt ? t("duo.undo", { defaultValue: "Undo DUO registration" }) : t("duo.complete", { defaultValue: "Mark registered with DUO" })}
                          </button>
                        </LdsTableCell>
                      </LdsTableRow>
                    );
                  })}
                </LdsTableBody>
              </LdsTable>
            </LdsTableScroll>
          </LdsTableCard>
        )}
      </LdsOverviewSection>
    </LdsOverviewPage>
  );
}
